import React, { useState, useEffect } from "react";
import axios from "axios";
import { connect } from "react-redux";
import { showError } from "../../toastify";
import {
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Typography,
  Box,
  CircularProgress,
  Alert,
  AlertTitle,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import { useHistory, useLocation } from "react-router-dom";

const ResetPassword = (props) => {
  const { user } = props;
  const location = useLocation();
  const history = useHistory();
  const queryParameters = new URLSearchParams(location.search);
  const token = queryParameters.get("token");

  const [credentials, setCredentials] = useState({
    password: "",
    confirmPassword: "",
    showPassword: false,
    showConfirmPassword: false,
  });
  const [loading, setLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [error, setError] = useState("");

  const checkToken = async (token) => {
    setError("");
    setIsTokenValid(false);
    setIsFetching(true);
    try {
      await axios.get("/api/auth/verify-password-reset-token/" + token);
      setIsTokenValid(true);
    } catch (error) {
      setError(error.response?.data || "Invalid password reset token");
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    if (!!token) checkToken(token);
    else history.push("/");
  }, [token]);

  const confirmResetPassword = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post("/api/auth/confirm-reset-password", {
        token: token,
        password: credentials.password,
        confirmPassword: credentials.confirmPassword,
      });
      setError("");
      setSuccessMessage(data.message);
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    confirmResetPassword();
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        background: "linear-gradient(135deg, #0d6efd20, #0a58ca30, #0d47a115)",
        padding: 2,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 400,
          background: "white",
          borderRadius: "16px",
          boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
          padding: 4,
        }}
      >
        {isFetching ? (
          <Box sx={{ textAlign: "center", py: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography
              variant="h5"
              component="h2"
              sx={{
                fontWeight: 700,
                textAlign: "center",
                mb: 3,
                background:
                  "linear-gradient(to right, #0d47a1, #0d6efd, #fd7e14)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Change Password
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                <AlertTitle>Error</AlertTitle>
                {error}
              </Alert>
            )}

            {successMessage ? (
              <Alert severity="success" sx={{ mb: 3 }}>
                <AlertTitle>Success</AlertTitle>
                {successMessage}
              </Alert>
            ) : (
              <Box component="form" onSubmit={onSubmit}>
                <FormControl fullWidth variant="outlined" sx={{ mb: 3 }}>
                  <InputLabel htmlFor="password">New Password</InputLabel>
                  <OutlinedInput
                    id="password"
                    type={credentials.showPassword ? "text" : "password"}
                    value={credentials.password}
                    onChange={(e) =>
                      setCredentials({
                        ...credentials,
                        password: e.target.value,
                      })
                    }
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            setCredentials({
                              ...credentials,
                              showPassword: !credentials.showPassword,
                            })
                          }
                          edge="end"
                        >
                          {credentials.showPassword ? (
                            <VisibilityOffIcon />
                          ) : (
                            <VisibilityIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    }
                    label="New Password"
                  />
                </FormControl>

                <FormControl fullWidth variant="outlined" sx={{ mb: 3 }}>
                  <InputLabel htmlFor="confirmPassword">
                    Confirm New Password
                  </InputLabel>
                  <OutlinedInput
                    id="confirmPassword"
                    type={credentials.showConfirmPassword ? "text" : "password"}
                    value={credentials.confirmPassword}
                    onChange={(e) =>
                      setCredentials({
                        ...credentials,
                        confirmPassword: e.target.value,
                      })
                    }
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            setCredentials({
                              ...credentials,
                              showConfirmPassword:
                                !credentials.showConfirmPassword,
                            })
                          }
                          edge="end"
                        >
                          {credentials.showConfirmPassword ? (
                            <VisibilityOffIcon />
                          ) : (
                            <VisibilityIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    }
                    label="Confirm New Password"
                  />
                </FormControl>

                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  disabled={
                    loading ||
                    credentials.password !== credentials.confirmPassword ||
                    !credentials.password
                  }
                  sx={{
                    background: "linear-gradient(to right, #e76f51, #0d6efd)",
                    color: "white",
                    padding: "12px",
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "16px",
                    color: "white",
                    "&:hover": {
                      background: "linear-gradient(to right, #e76f51, #0a58ca)",
                    },
                    "&:disabled": {
                      background: "linear-gradient(to right, #e76f51, #0d6efd)",
                      color: "white",
                      opacity: 0.7,
                    },
                  }}
                >
                  {loading ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CircularProgress
                        size={20}
                        color="inherit"
                        sx={{ mr: 1 }}
                      />
                      Processing...
                    </Box>
                  ) : (
                    "Change Password"
                  )}
                </Button>
              </Box>
            )}

            <Button
              fullWidth
              variant="text"
              startIcon={<KeyboardDoubleArrowLeftIcon />}
              onClick={() => history.push("/")}
              sx={{
                mt: 3,
                textTransform: "none",
                color: "primary.main",
                "&:hover": {
                  color: "#fd7e14",
                },
              }}
            >
              Go Back to Home
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
};

const mapStateToProps = ({ user, loading }) => {
  return { user, loading };
};
export default connect(mapStateToProps, {})(ResetPassword);
